import { AuthLayoutContainer } from 'components/Layouts/AuthLayout';
import { Typography, Button, IconGraphy, colors, Input } from '@leapeasy/ui-kit';
import { Box } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { useCallback, useState, useEffect, useRef } from 'react';
import { verify2FACode, mfaUpdateResult, send2FACode } from 'store/actions/authActions';

const RESEND_COOLDOWN_SECONDS = 60 * 5;

const formatTimer = (seconds) => {
  const safeSeconds = Math.max(0, seconds);
  const mins = Math.floor(safeSeconds / 60);
  const secs = safeSeconds % 60;
  return `${mins}:${String(secs).padStart(2, '0')}`;
};

export const Otp = ({ target, onBack, onSuccess }) => {
  const dispatch = useDispatch();

  const [key, setKey] = useState(0);
  const loading = useSelector((state) => state.getIn(['auth', 'loading']));
  const mfaVerification = useSelector((state) => state.getIn(['auth', 'mfaVerification']));
  const [error, setError] = useState(false);
  const [secondsRemaining, setSecondsRemaining] = useState(RESEND_COOLDOWN_SECONDS);

  const timerIdRef = useRef(null);
  const verifyingRef = useRef(false);

  const clearTimer = useCallback(() => {
    if (timerIdRef.current) {
      clearInterval(timerIdRef.current);
      timerIdRef.current = null;
    }
  }, []);

  const startTimer = useCallback(() => {
    clearTimer();
    setSecondsRemaining(RESEND_COOLDOWN_SECONDS);

    timerIdRef.current = setInterval(() => {
      setSecondsRemaining((prev) => {
        if (prev <= 1) {
          clearTimer();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  }, [clearTimer]);

  useEffect(() => {
    setKey((current) => current + 1);
    setError(false);
    verifyingRef.current = false;
    startTimer();

    return () => {
      clearTimer();
    };
  }, [clearTimer, startTimer]);

  useEffect(() => {
    if (mfaVerification === 'success') {
      dispatch(mfaUpdateResult('none'));
      verifyingRef.current = false;
      onSuccess();
    } else if (mfaVerification === 'failed') {
      setError(true);
      verifyingRef.current = false;
    }
  }, [dispatch, mfaVerification, onSuccess]);

  const onResendCode = useCallback(() => {
    if (loading) return;

    setKey((current) => current + 1);
    setError(false);
    verifyingRef.current = false;
    startTimer();
    dispatch(send2FACode(target));
  }, [dispatch, loading, startTimer, target]);

  const onChangeOtp = (value) => {
    const digitsOnly = String(value || '')
      .replace(/\D/g, '')
      .slice(0, 6);
    if (digitsOnly.length !== 6 || loading || verifyingRef.current) {
      return;
    }

    setError(false);
    verifyingRef.current = true;
    dispatch(verify2FACode({ ...target, code: digitsOnly }));
    dispatch(mfaUpdateResult('none'));
  };

  const deliveryTarget =
    target?.mfa_method === 'email' ? target?.email || 'your email' : target?.phone || 'your phone';

  return (
    <AuthLayoutContainer showGrayLogo>
      <Box display="flex" flexDirection="column" gap="24px">
        <Box
          sx={{
            width: 128,
            height: 128,
            borderRadius: '50%',
            background: colors.purple[100],
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <IconGraphy
            icon={
              target?.mfa_method === 'email'
                ? 'Notifications.MarkMailRead'
                : target?.mfa_method === 'sms'
                  ? 'Notification.MarkChatUnread'
                  : 'MediaDevices.Phone'
            }
            width={63}
            height={54}
            style={{ color: colors.purple[700] }}
          />
        </Box>

        <Box display="flex" flexDirection="column" gap="12px">
          <Typography variant="h2" style={{ color: colors.purple[900], fontWeight: 500 }}>
            We've sent you a code
          </Typography>

          <Typography
            variant="body1"
            style={{
              color: colors.neutral[900],
              fontWeight: 400,
              display: 'flex',
              gap: '4px',
              flexWrap: 'wrap',
            }}
          >
            The code was sent to you via
            <Typography style={{ color: colors.purple[900], fontWeight: '500' }}>
              {target?.mfa_method || 'verification'}
            </Typography>
            at
            <Typography style={{ color: colors.purple[900], fontWeight: '500' }}>
              {deliveryTarget}
            </Typography>
          </Typography>
        </Box>

        <Box sx={{ border: '1px solid #F8F4F9', height: '0px' }} />

        <Box display="flex" justifyContent="center">
          <Input
            key={key}
            onChange={(e) => onChangeOtp(e.target.value)}
            type="otp"
            fullWidth
            size="medium"
            otpLength={6}
            helperMessage={error ? 'The code you entered is wrong.' : ''}
            errorType={error ? 'error' : 'none'}
            showHelperMessage
          />
        </Box>

        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            flexDirection: 'row',
            gap: '16px',
            mt: 2,
          }}
        >
          <Button
            size="medium"
            variant="tertiary"
            onClick={() => onBack()}
            style={{ flexShrink: 2 }}
          >
            Back
          </Button>

          <Button
            variant="primary"
            size="medium"
            onClick={onResendCode}
            style={{ flexShrink: 1 }}
            disabled={loading}
          >
            {secondsRemaining > 0
              ? `Resend code in ${formatTimer(secondsRemaining)}`
              : 'Resend code'}
          </Button>
        </Box>
      </Box>
    </AuthLayoutContainer>
  );
};
